import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/documentation/documentation/node_modules/gatsby-theme-doctornpm/src/components/layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h3>{`Description`}</h3>
    <p><strong parentName="p">{`Workspaces`}</strong>{` is a generic term that refers to the set of features in the
npm cli that provides support to managing multiple packages from your local
file system from within a singular top-level, root package.`}</p>
    <p>{`This set of features makes up for a much more streamlined workflow handling
linked packages from the local file system. Automating the linking process
as part of `}<inlineCode parentName="p">{`npm install`}</inlineCode>{` and avoiding manually having to use `}<inlineCode parentName="p">{`npm link`}</inlineCode>{` in
order to add references to packages that should be symlinked into the current
`}<inlineCode parentName="p">{`node_modules`}</inlineCode>{` folder.`}</p>
    <p>{`We also refer to these packages being auto-symlinked during `}<inlineCode parentName="p">{`npm install`}</inlineCode>{` as a
single `}<strong parentName="p">{`workspace`}</strong>{`, meaning it's a nested package within the current local
file system that is explicitly defined in the `}<a parentName="p" {...{
        "href": "/cli/v8/configuring-npm/package-json#workspaces"
      }}><inlineCode parentName="a">{`package.json`}</inlineCode></a>{`
`}<inlineCode parentName="p">{`workspaces`}</inlineCode>{` configuration.`}</p>
    <h3>{`Defining workspaces`}</h3>
    <p>{`Workspaces are usually defined via the `}<inlineCode parentName="p">{`workspaces`}</inlineCode>{` property of the
`}<a parentName="p" {...{
        "href": "/cli/v8/configuring-npm/package-json#workspaces"
      }}><inlineCode parentName="a">{`package.json`}</inlineCode></a>{` file, e.g:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "name": "my-workspaces-powered-project",
  "workspaces": [
    "packages/a"
  ]
}
`}</code></pre>
    <p>{`Given the above `}<inlineCode parentName="p">{`package.json`}</inlineCode>{` example living at a current working
directory `}<inlineCode parentName="p">{`.`}</inlineCode>{` that contains a folder named `}<inlineCode parentName="p">{`packages/a`}</inlineCode>{` that itself contains
a `}<inlineCode parentName="p">{`package.json`}</inlineCode>{` inside it, defining a Node.js package, e.g:`}</p>
    <pre><code parentName="pre" {...{}}>{`.
+-- package.json
\`-- packages
   +-- a
   |   \`-- package.json
`}</code></pre>
    <p>{`The expected result once running `}<inlineCode parentName="p">{`npm install`}</inlineCode>{` in this current working
directory `}<inlineCode parentName="p">{`.`}</inlineCode>{` is that the folder `}<inlineCode parentName="p">{`packages/a`}</inlineCode>{` will get symlinked to the
`}<inlineCode parentName="p">{`node_modules`}</inlineCode>{` folder of the current working dir.`}</p>
    <p>{`Below is a post `}<inlineCode parentName="p">{`npm install`}</inlineCode>{` example, given that same previous example
structure of files and folders:`}</p>
    <pre><code parentName="pre" {...{}}>{`.
+-- node_modules
|  \`-- a -> ../packages/a
+-- package-lock.json
+-- package.json
\`-- packages
   +-- a
   |   \`-- package.json
`}</code></pre>
    <h3>{`Getting started with workspaces`}</h3>
    <p>{`You may automate the required steps to define a new workspace using
`}<a parentName="p" {...{
        "href": "/cli/v8/commands/npm-init"
      }}>{`npm init`}</a>{`. For example in a project that already has a
`}<inlineCode parentName="p">{`package.json`}</inlineCode>{` defined you can run:`}</p>
    <pre><code parentName="pre" {...{}}>{`npm init -w ./packages/a
`}</code></pre>
    <p>{`This command will create the missing folders and a new `}<inlineCode parentName="p">{`package.json`}</inlineCode>{`
file (if needed) while also making sure to properly configure the
`}<inlineCode parentName="p">{`"workspaces"`}</inlineCode>{` property of your root project `}<inlineCode parentName="p">{`package.json`}</inlineCode>{`.`}</p>
    <h3>{`Adding dependencies to a workspace`}</h3>
    <p>{`It's possible to directly add/remove/update dependencies of your workspaces
using the `}<a parentName="p" {...{
        "href": "/cli/v8/using-npm/config#workspace"
      }}><inlineCode parentName="a">{`workspace`}</inlineCode>{` config`}</a>{`.`}</p>
    <p>{`For example, assuming the following structure:`}</p>
    <pre><code parentName="pre" {...{}}>{`.
+-- package.json
\`-- packages
   +-- a
   |   \`-- package.json
   \`-- b
       \`-- package.json
`}</code></pre>
    <p>{`If you want to add a dependency named `}<inlineCode parentName="p">{`abbrev`}</inlineCode>{` from the registry as a
dependency of your workspace `}<strong parentName="p">{`a`}</strong>{`, you may use the workspace config to tell
the npm installer that package should be added as a dependency of the provided
workspace:`}</p>
    <pre><code parentName="pre" {...{}}>{`npm install abbrev -w a
`}</code></pre>
    <p>{`Note: other installing commands such as `}<inlineCode parentName="p">{`uninstall`}</inlineCode>{`, `}<inlineCode parentName="p">{`ci`}</inlineCode>{`, etc will also
respect the provided `}<inlineCode parentName="p">{`workspace`}</inlineCode>{` configuration.`}</p>
    <h3>{`Using workspaces`}</h3>
    <p>{`Given the `}<a parentName="p" {...{
        "href": "https://nodejs.org/dist/latest-v14.x/docs/api/modules.html#modules_all_together"
      }}>{`specifities of how Node.js handles module resolution`}</a>{` it's possible to consume any defined workspace
by its declared `}<inlineCode parentName="p">{`package.json`}</inlineCode>{` `}<inlineCode parentName="p">{`name`}</inlineCode>{`. Continuing from the example defined
above, let's also create a Node.js script that will require the workspace `}<inlineCode parentName="p">{`a`}</inlineCode>{`
example module, e.g:`}</p>
    <pre><code parentName="pre" {...{}}>{`// ./packages/a/index.js
module.exports = 'a'

// ./lib/index.js
const moduleA = require('a')
console.log(moduleA) // -> a
`}</code></pre>
    <p>{`When running it with:`}</p>
    <p><inlineCode parentName="p">{`node lib/index.js`}</inlineCode></p>
    <p>{`This demonstrates how the nature of `}<inlineCode parentName="p">{`node_modules`}</inlineCode>{` resolution allows for
`}<strong parentName="p">{`workspaces`}</strong>{` to enable a portable workflow for requiring each `}<strong parentName="p">{`workspace`}</strong>{`
in such a way that is also easy to `}<a parentName="p" {...{
        "href": "/cli/v8/commands/npm-publish"
      }}>{`publish`}</a>{` these
nested workspaces to be consumed elsewhere.`}</p>
    <h3>{`Running commands in the context of workspaces`}</h3>
    <p>{`You can use the `}<inlineCode parentName="p">{`workspace`}</inlineCode>{` configuration option to run commands in the context
of a configured workspace.
Additionally, if your current directory is in a workspace, the `}<inlineCode parentName="p">{`workspace`}</inlineCode>{`
configuration is implicitly set, and `}<inlineCode parentName="p">{`prefix`}</inlineCode>{` is set to the root workspace.`}</p>
    <p>{`Following is a quick example on how to use the `}<inlineCode parentName="p">{`npm run`}</inlineCode>{` command in the context
of nested workspaces. For a project containing multiple workspaces, e.g:`}</p>
    <pre><code parentName="pre" {...{}}>{`.
+-- package.json
\`-- packages
   +-- a
   |   \`-- package.json
   \`-- b
       \`-- package.json
`}</code></pre>
    <p>{`By running a command using the `}<inlineCode parentName="p">{`workspace`}</inlineCode>{` option, it's possible to run the
given command in the context of that specific workspace. e.g:`}</p>
    <pre><code parentName="pre" {...{}}>{`npm run test --workspace=a
`}</code></pre>
    <p>{`You could also run the command within the workspace.`}</p>
    <pre><code parentName="pre" {...{}}>{`cd packages/a && npm run test
`}</code></pre>
    <p>{`Either will run the `}<inlineCode parentName="p">{`test`}</inlineCode>{` script defined within the
`}<inlineCode parentName="p">{`./packages/a/package.json`}</inlineCode>{` file.`}</p>
    <p>{`Please note that you can also specify this argument multiple times in the
command-line in order to target multiple workspaces, e.g:`}</p>
    <pre><code parentName="pre" {...{}}>{`npm run test --workspace=a --workspace=b
`}</code></pre>
    <p>{`It's also possible to use the `}<inlineCode parentName="p">{`workspaces`}</inlineCode>{` (plural) configuration option to
enable the same behavior but running that command in the context of `}<strong parentName="p">{`all`}</strong>{`
configured workspaces. e.g:`}</p>
    <pre><code parentName="pre" {...{}}>{`npm run test --workspaces
`}</code></pre>
    <p>{`Will run the `}<inlineCode parentName="p">{`test`}</inlineCode>{` script in both `}<inlineCode parentName="p">{`./packages/a`}</inlineCode>{` and `}<inlineCode parentName="p">{`./packages/b`}</inlineCode>{`.`}</p>
    <p>{`Commands will be run in each workspace in the order they appear in your `}<inlineCode parentName="p">{`package.json`}</inlineCode></p>
    <pre><code parentName="pre" {...{}}>{`{
  "workspaces": [ "packages/a", "packages/b" ]
}
`}</code></pre>
    <p>{`Order of run is different with:`}</p>
    <pre><code parentName="pre" {...{}}>{`{
  "workspaces": [ "packages/b", "packages/a" ]
}
`}</code></pre>
    <h3>{`Ignoring missing scripts`}</h3>
    <p>{`It is not required for all of the workspaces to implement scripts run with the `}<inlineCode parentName="p">{`npm run`}</inlineCode>{` command.`}</p>
    <p>{`By running the command with the `}<inlineCode parentName="p">{`--if-present`}</inlineCode>{` flag, npm will ignore workspaces missing target script.`}</p>
    <pre><code parentName="pre" {...{}}>{`npm run test --workspaces --if-present
`}</code></pre>
    <h3>{`See also`}</h3>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/commands/npm-install"
        }}>{`npm install`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/commands/npm-publish"
        }}>{`npm publish`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/commands/npm-run-script"
        }}>{`npm run-script`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/using-npm/config"
        }}>{`config`}</a></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      